import React from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../hooks';
import { appsData } from '../data/appsData';

const siteDocs = [
  {
    icon: '🔒',
    title: 'Privacy Policy',
    subtitle: 'All Riven Byte Apps',
    desc: 'How we handle your data across all our games. Spoiler: we barely collect any.',
    path: '/privacy',
    updated: 'May 27, 2026',
    color: 'from-secondary-400/20 to-secondary-600/5 border-secondary-400/20 hover:border-secondary-400/50',
    badge: 'General',
    badgeColor: 'bg-secondary-400/15 text-secondary-400 border-secondary-400/20',
  },
  {
    icon: '📜',
    title: 'Terms of Service',
    subtitle: 'Usage Agreement',
    desc: 'The rules of the game — what you can and cannot do when using our products.',
    path: '/terms',
    updated: 'May 27, 2026',
    color: 'from-primary-500/20 to-primary-600/5 border-primary-500/20 hover:border-primary-500/50',
    badge: 'General',
    badgeColor: 'bg-primary-500/15 text-primary-400 border-primary-500/20',
  },
  {
    icon: '🍪',
    title: 'Cookie Policy',
    subtitle: 'Website Cookies',
    desc: 'Information about the cookies used on this website and how to manage them.',
    path: '/cookies',
    updated: 'May 27, 2026',
    color: 'from-yellow-500/20 to-yellow-600/5 border-yellow-500/20 hover:border-yellow-500/50',
    badge: 'Website',
    badgeColor: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
  },
];

const AppCard = ({ app }) => {
  const privacyPath = app.customPrivacyPath || `/apps/${app.slug}/privacy`;
  const termsPath = `/apps/${app.slug}/terms`;
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/3 p-5 flex flex-col gap-3 hover:border-white/20 transition-all"
      style={{ borderLeftColor: app.accentColor, borderLeftWidth: '3px' }}
    >
      <div className="flex items-center gap-3">
        <span className="text-3xl">{app.icon}</span>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white text-sm leading-tight truncate">{app.name}</h3>
          <p className="text-gray-600 text-xs font-mono truncate">{app.packageName}</p>
        </div>
        <span
          className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium"
          style={{ background: app.accentColor + '20', color: app.accentColor }}
        >
          {app.category}
        </span>
      </div>
      <p className="text-gray-500 text-xs leading-relaxed">{app.description}</p>
      <div className="flex gap-2 mt-1">
        <Link
          to={privacyPath}
          className="flex-1 text-center text-xs py-2 rounded-lg font-medium border border-white/10 hover:border-white/25 text-gray-300 hover:text-white transition-colors"
        >
          🔒 Privacy Policy
        </Link>
        <Link
          to={termsPath}
          className="flex-1 text-center text-xs py-2 rounded-lg font-medium border border-white/10 hover:border-white/25 text-gray-300 hover:text-white transition-colors"
        >
          📜 Terms of Service
        </Link>
      </div>
    </div>
  );
};

const LegalHubPage = () => {
  const [heroRef, heroInView] = useInView(0.1);
  const [cardsRef, cardsInView] = useInView(0.05);
  const [appsRef, appsInView] = useInView(0.05);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 md:py-20 relative african-pattern">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black" />
        <div
          ref={heroRef}
          className={`relative z-10 max-w-4xl mx-auto px-6 text-center transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <p className="section-subtitle text-secondary-400">TRANSPARENCY FIRST</p>
          <h1 className="section-title mb-6">
            LEGAL & <span className="text-gradient">PRIVACY</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Every legal document, privacy policy, and terms agreement for all Riven Byte
            products — web, mobile, and beyond. One place, full transparency.
          </p>
        </div>
      </section>

      {/* Info banner */}
      <div className="bg-secondary-400/8 border-y border-secondary-400/15 py-5">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-gray-300">
            <span className="text-secondary-400 text-xl">ℹ️</span>
            <span>Our apps collect <strong className="text-white">no personal data</strong> and require no account or login.</span>
          </div>
          <a
            href="mailto:paulnkingwa34@gmail.com"
            className="text-secondary-400 hover:text-secondary-300 text-sm font-medium whitespace-nowrap transition-colors"
          >
            Legal questions? Email us →
          </a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16">

        {/* ── Site-wide documents ─────────────────────────────── */}
        <section className="mb-20">
          <div className="mb-8">
            <p className="text-xs font-bold tracking-widest uppercase text-secondary-400 mb-1">Site-Wide</p>
            <h2 className="text-2xl font-bold text-white">General Documents</h2>
          </div>
          <div
            ref={cardsRef}
            className={`grid sm:grid-cols-3 gap-6 transition-all duration-1000 ${cardsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {siteDocs.map((doc) => (
              <Link
                key={doc.path}
                to={doc.path}
                className={`group bg-gradient-to-br ${doc.color} border rounded-3xl p-7 flex flex-col gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl`}
              >
                <div className="flex items-start justify-between">
                  <div className="text-4xl">{doc.icon}</div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${doc.badgeColor}`}>
                    {doc.badge}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">{doc.title}</h3>
                  <p className="text-xs text-gray-500 mb-2 font-gaming tracking-wider uppercase">{doc.subtitle}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{doc.desc}</p>
                </div>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/10">
                  <span className="text-xs text-gray-500">Updated: {doc.updated}</span>
                  <span className="text-sm font-semibold text-white flex items-center gap-1">
                    Read <span className="group-hover:translate-x-1 transition-transform inline-block">→</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* ── Per-App documents ──────────────────────────────── */}
        <section className="mb-20">
          <div className="mb-8">
            <p className="text-xs font-bold tracking-widest uppercase text-primary-500 mb-1">Google Play · Android</p>
            <h2 className="text-2xl font-bold text-white">
              App-Specific <span className="text-gradient">Privacy & Terms</span>
            </h2>
            <p className="text-gray-500 text-sm mt-2">
              Each app published on Google Play has its own dedicated privacy policy and terms of service.
            </p>
          </div>
          <div
            ref={appsRef}
            className={`grid sm:grid-cols-2 lg:grid-cols-2 gap-5 transition-all duration-1000 ${appsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {appsData.map((app) => (
              <AppCard key={app.slug} app={app} />
            ))}
          </div>
        </section>

        {/* ── App-ads.txt status ─────────────────────────────── */}
        <section className="mb-20">
          <div className="mb-6">
            <p className="text-xs font-bold tracking-widest uppercase text-yellow-400 mb-1">AdMob Setup</p>
            <h2 className="text-2xl font-bold text-white">App-Ads.txt</h2>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/3 p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">📄</span>
              <div>
                <p className="font-mono text-sm text-white">https://riven-byte.pages.dev/app-ads.txt</p>
                <p className="text-xs text-gray-500 mt-0.5">Authorizes Google AdMob to serve ads on all Riven Byte apps</p>
              </div>
              <span className="ml-auto text-xs px-3 py-1 rounded-full bg-green-500/15 text-green-400 border border-green-500/20 flex-shrink-0">
                ✓ Active
              </span>
            </div>
            <div className="bg-black/40 rounded-xl p-4 font-mono text-xs text-green-400 border border-white/5">
              google.com, pub-9545001934271348, DIRECT, f08c47fec0942fa0
            </div>
            <div className="mt-4 p-4 rounded-xl bg-yellow-500/8 border border-yellow-500/20">
              <p className="text-yellow-300 text-xs font-bold mb-1">⚠️ Slingshot Showdown — Action Required</p>
              <p className="text-gray-400 text-xs leading-relaxed">
                <code className="text-yellow-400">com.slingshotshowdown.game</code> shows "No app-ads.txt found" in AdMob.
                Go to <strong className="text-white">Google Play Console → Slingshot Showdown → Store presence → Store listing</strong> and
                set the <strong className="text-white">Website URL</strong> field to{' '}
                <code className="text-yellow-400">https://riven-byte.pages.dev</code>. AdMob re-crawls within 24 hours.
              </p>
            </div>
          </div>
        </section>

        {/* ── Commitment ────────────────────────────────────── */}
        <div className="bg-gradient-to-br from-white/5 to-white/2 rounded-3xl border border-white/10 p-10 text-center">
          <div className="text-5xl mb-5">🛡️</div>
          <h2 className="font-gaming text-2xl font-bold text-white mb-4">OUR PRIVACY COMMITMENT</h2>
          <p className="text-gray-400 leading-relaxed max-w-2xl mx-auto mb-8">
            At Riven Byte, privacy isn't a checkbox — it's a principle. Our games work fully
            offline, store data only on your device, and will never sell or share your information
            with third parties beyond what's required to show ads through Google AdMob.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: '🚫', text: 'No accounts required' },
              { icon: '📵', text: 'Full offline support' },
              { icon: '💾', text: 'Local storage only' },
            ].map((item) => (
              <div key={item.text} className="bg-white/5 rounded-2xl border border-white/10 px-4 py-5 text-center">
                <div className="text-3xl mb-2">{item.icon}</div>
                <p className="text-sm text-gray-300 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalHubPage;
